import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSave, 
  faTimes, 
  faPencilAlt,
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-hot-toast';
import { updateFirebaseData } from '../../utils/firebaseUtils';
import useSiteData from '../../hooks/useSiteData';

const EditCatering = () => {
  const { data, isLoading: isSiteDataLoading, refetch } = useSiteData();
  const [formData, setFormData] = useState({
    miniumDaysAdvance: 2,
    minimumPeople: 10
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (data?.catering) {
      setFormData(data.catering);
    }
  }, [data]);

  const handleChange = (field, value) => {
    const numValue = parseInt(value, 10) || 0;
    setFormData(prev => ({
      ...prev,
      [field]: numValue
    }));
  };

  const handleSubmit = async () => {
    try {
      if (formData.miniumDaysAdvance < 1 || formData.minimumPeople < 1) {
        toast.error('Values must be greater than 0');
        return;
      }

      setIsLoading(true);
      const success = await updateFirebaseData('catering', formData);
      
      if (success) {
        toast.success('Catering information updated successfully!');
        setError('');
        setIsEditing(false);
        refetch();
      } else {
        throw new Error('Failed to update catering information');
      }
    } catch (error) {
      console.error('Error updating catering info:', error);
      setError('Failed to update catering information.');
      toast.error('Failed to update catering information.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderReadOnlyMode = () => (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Catering Information</h2>
        <button
          onClick={() => setIsEditing(true)}
          className="px-4 py-2 text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 flex items-center"
        >
          <FontAwesomeIcon icon={faPencilAlt} className="mr-2" />
          Edit
        </button>
      </div>
      
      <div className="space-y-4">
        <div>
          <h3 className="font-medium">Minimum Days Advance Notice</h3>
          <p className="text-gray-600">{formData.miniumDaysAdvance} days</p>
        </div>
        
        <div>
          <h3 className="font-medium">Minimum Number of People</h3>
          <p className="text-gray-600">{formData.minimumPeople} people</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex-1 min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <h1 className="text-2xl font-bold mb-6">Edit Catering Information</h1>
        
        {error && (
          <div className="bg-red-50 text-red-500 p-4 rounded-lg mb-6">
            {error}
          </div>
        )}
        
        {isEditing ? (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h2 className="text-xl font-semibold mb-6">Edit Catering Details</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Minimum Days Advance Notice
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.miniumDaysAdvance}
                    onChange={(e) => handleChange('miniumDaysAdvance', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Minimum Number of People
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.minimumPeople}
                    onChange={(e) => handleChange('minimumPeople', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setIsEditing(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                <FontAwesomeIcon icon={faTimes} className="mr-2" />
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={isLoading}
                className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-blue-400"
              >
                <FontAwesomeIcon icon={faSave} className="mr-2" />
                {isLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        ) : (
          renderReadOnlyMode()
        )}
      </div>
    </div>
  );
};

export default EditCatering;
